// Data validation utilities to ensure frontend-backend consistency

export class ValidationError extends Error {
  constructor(field: string, message: string) {
    super(`Validation error for field '${field}': ${message}`);
    this.name = 'ValidationError';
  }
}

// Validation rules
export const validation = {
  // Phone number validation (supports Chinese phone numbers)
  phone: (value: string): boolean => {
    if (!value) return true; // Optional field
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(value);
  },

  // Email validation
  email: (value: string): boolean => {
    if (!value) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },

  // Required field validation
  required: (value: any): boolean => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return value !== null && value !== undefined;
  },

  // Case number format validation
  caseNumber: (value: string): boolean => {
    if (!value) return true; // Optional, will be auto-generated
    // Assuming format: YYYY-MM-DD-XXXX or similar
    const caseNumberRegex = /^[\w-]+$/;
    return caseNumberRegex.test(value);
  },

  // Lead number format validation
  leadNumber: (value: string): boolean => {
    if (!value) return true; // Optional, will be auto-generated
    const leadNumberRegex = /^[\w-]+$/;
    return leadNumberRegex.test(value);
  },

  // Date format validation (ISO string)
  date: (value: string): boolean => {
    if (!value) return true; // Optional field
    const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
    return dateRegex.test(value) && !isNaN(Date.parse(value));
  },

  // Numeric validation
  number: (value: any): boolean => {
    return !isNaN(Number(value)) && isFinite(Number(value));
  },

  // Positive number validation
  positiveNumber: (value: any): boolean => {
    return validation.number(value) && Number(value) > 0;
  },

  // Chinese ID card validation (18 digits)
  idCard: (value: string): boolean => {
    if (!value) return true; // Optional field
    const idCardRegex = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/;
    return idCardRegex.test(value);
  },

  // Unified social credit code validation (18 characters)
  creditCode: (value: string): boolean => {
    if (!value) return true; // Optional field
    const creditCodeRegex = /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/;
    return creditCodeRegex.test(value);
  },

  // Identity number validation (ID card or credit code)
  identityNumber: (value: string, type?: string): boolean => {
    if (!value) return true; // Optional field
    if (type === '身份证') {
      return validation.idCard(value);
    } else if (type === '统一社会信用代码') {
      return validation.creditCode(value);
    }
    // If type not specified, try both
    return validation.idCard(value) || validation.creditCode(value);
  },

  // Amount validation (non-negative)
  amount: (value: any): boolean => {
    return validation.number(value) && Number(value) >= 0;
  },

  // Date range validation (end date should be after start date)
  dateRange: (startDate: any, endDate: any): boolean => {
    if (!startDate || !endDate) return true; // Optional
    const start = new Date(startDate);
    const end = new Date(endDate);
    return end >= start;
  }
};

// Lead data validation
export const validateLeadData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!validation.required(data.contact_name)) {
    errors.push('联系人姓名不能为空');
  }

  if (data.phone && !validation.phone(data.phone)) {
    errors.push('手机号格式不正确');
  }

  if (data.lead_number && !validation.leadNumber(data.lead_number)) {
    errors.push('线索编号格式不正确');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Case data validation (comprehensive)
export const validateCaseData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required fields
  if (!validation.required(data.case_name)) {
    errors.push('案件名称不能为空');
  }

  if (!validation.required(data.case_type)) {
    errors.push('案件类型不能为空');
  }

  if (!validation.required(data.case_stage)) {
    errors.push('案件阶段不能为空');
  }

  if (!validation.required(data.accept_date)) {
    errors.push('承接日期不能为空');
  }

  if (!validation.required(data.responsible_lawyer_id)) {
    errors.push('负责律师不能为空');
  }

  // Date validations
  if (data.accept_date && !validation.date(data.accept_date)) {
    errors.push('承接日期格式不正确');
  }

  if (data.court_date && !validation.date(data.court_date)) {
    errors.push('开庭时间格式不正确');
  }

  if (data.verdict_date && !validation.date(data.verdict_date)) {
    errors.push('判决日期格式不正确');
  }

  // Date logic validation
  if (data.accept_date && data.court_date) {
    if (!validation.dateRange(data.accept_date, data.court_date)) {
      errors.push('开庭时间不能早于承接日期');
    }
  }

  if (data.accept_date && data.verdict_date) {
    if (!validation.dateRange(data.accept_date, data.verdict_date)) {
      errors.push('判决日期不能早于承接日期');
    }
  }

  if (data.court_date && data.verdict_date) {
    if (!validation.dateRange(data.court_date, data.verdict_date)) {
      errors.push('判决日期不能早于开庭时间');
    }
  }

  // Amount validations
  if (data.contract_amount !== undefined && !validation.amount(data.contract_amount)) {
    errors.push('合同金额必须是非负数');
  }

  if (data.received_amount !== undefined && !validation.amount(data.received_amount)) {
    errors.push('已收金额必须是非负数');
  }

  if (data.contract_amount !== undefined && data.received_amount !== undefined) {
    if (Number(data.received_amount) > Number(data.contract_amount)) {
      errors.push('已收金额不能大于合同金额');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Case party validation
export const validateCaseParty = (party: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!validation.required(party.party_role)) {
    errors.push('当事人角色不能为空');
  }

  if (!validation.required(party.name)) {
    errors.push('当事人姓名不能为空');
  }

  if (!validation.required(party.identity_number)) {
    errors.push('身份证号/统一社会信用代码不能为空');
  }

  if (party.identity_number && party.identity_type) {
    if (!validation.identityNumber(party.identity_number, party.identity_type)) {
      if (party.identity_type === '身份证') {
        errors.push('身份证号格式不正确');
      } else if (party.identity_type === '统一社会信用代码') {
        errors.push('统一社会信用代码格式不正确');
      } else {
        errors.push('身份证号/统一社会信用代码格式不正确');
      }
    }
  }

  if (party.phone && !validation.phone(party.phone)) {
    errors.push('联系电话格式不正确');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// User data validation
export const validateUserData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!validation.required(data.username)) {
    errors.push('用户名不能为空');
  }

  if (!validation.required(data.email) || !validation.email(data.email)) {
    errors.push('邮箱格式不正确');
  }

  if (data.phone && !validation.phone(data.phone)) {
    errors.push('手机号格式不正确');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Sanitize data for API requests (remove undefined/null values that might cause issues)
export const sanitizeApiData = (data: any): any => {
  const sanitized: any = {};

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null && value !== '') {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

// Convert frontend date format to backend format if needed
export const formatDateForApi = (date: any): string | undefined => {
  if (!date) return undefined;

  // If it's a dayjs object
  if (date && typeof date.format === 'function') {
    return date.format('YYYY-MM-DD');
  }

  // If it's already a string in correct format
  if (typeof date === 'string' && validation.date(date)) {
    return date;
  }

  return undefined;
};

// Ant Design Form validation rules
export const formRules = {
  required: (message: string = '此字段为必填项') => ({
    required: true,
    message,
  }),

  phone: {
    pattern: /^1[3-9]\d{9}$/,
    message: '请输入正确的手机号码',
  },

  email: {
    type: 'email' as const,
    message: '请输入正确的邮箱地址',
  },

  idCard: {
    pattern: /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/,
    message: '请输入正确的身份证号',
  },

  creditCode: {
    pattern: /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/,
    message: '请输入正确的统一社会信用代码',
  },

  positiveNumber: {
    validator: (_: any, value: any) => {
      if (!value && value !== 0) return Promise.resolve();
      if (isNaN(Number(value)) || Number(value) <= 0) {
        return Promise.reject(new Error('请输入大于0的数字'));
      }
      return Promise.resolve();
    },
  },

  nonNegativeNumber: {
    validator: (_: any, value: any) => {
      if (!value && value !== 0) return Promise.resolve();
      if (isNaN(Number(value)) || Number(value) < 0) {
        return Promise.reject(new Error('请输入非负数'));
      }
      return Promise.resolve();
    },
  },

  // Custom validator for date range
  dateAfter: (startDateField: string, message: string = '结束日期不能早于开始日期') => ({
    validator: (_: any, value: any, callback: any) => {
      const form = _.field?.split('.')[0]; // Get form instance
      if (!value) return Promise.resolve();
      
      return {
        validator: (_: any, value: any) => {
          // This will be used with form instance
          return Promise.resolve();
        },
      };
    },
  }),

  // Identity number validator (ID card or credit code)
  identityNumber: (identityTypeField?: string) => ({
    validator: (_: any, value: any) => {
      if (!value) return Promise.resolve();
      
      // Try to validate as both ID card and credit code
      const isValidIdCard = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/.test(value);
      const isValidCreditCode = /^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/.test(value);
      
      if (!isValidIdCard && !isValidCreditCode) {
        return Promise.reject(new Error('请输入正确的身份证号或统一社会信用代码'));
      }
      
      return Promise.resolve();
    },
  }),

  // Amount validation (received <= contract)
  receivedAmount: (contractAmountField: string = 'contract_amount') => ({
    validator: (rule: any, value: any, callback: any, form: any) => {
      if (!value && value !== 0) return Promise.resolve();
      
      // This validator needs access to form values
      // Will be implemented in component with form.getFieldValue
      return Promise.resolve();
    },
  }),
};